import type {ComponentProps, ReactNode} from 'react';
import {Loader2, Search, X} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {Button} from '../button';
import {HelpTooltip} from '../help-tooltip';
import {LegacyInput} from '../legacy-fields/input';
import {cn} from '../lib/cn';

export type SearchInputProps = ComponentProps<'input'> & {
  className?: string;
  invalid?: boolean;
  label?: string;
  loading?: boolean;
  onReset?: () => void;
  optional?: boolean | string;
  tooltip?: ReactNode;
  wrapperClassName?: string;
};

export function SearchInput({
  disabled,
  invalid,
  label,
  loading,
  onReset,
  optional,
  placeholder,
  tooltip,
  wrapperClassName,
  className,
  ...props
}: SearchInputProps) {
  const {t} = useTranslation();
  const placeholderText = placeholder || `${t('search_property')}...`;
  const isBlocked = Boolean(disabled) || Boolean(loading);
  const optionalLabel = optional
    ? typeof optional === 'string'
      ? optional
      : t('optional')
    : undefined;
  const hasLabelMeta = Boolean(optionalLabel) || Boolean(tooltip);

  return (
    <div className={cn('input-wrapper', wrapperClassName)}>
      {(label || hasLabelMeta) && (
        <div className="mb-2 inline-flex max-w-full items-center gap-1.5 text-sm font-medium text-[var(--chekin-color-brand-navy)]">
          {label && <span className="min-w-0 truncate">{label}</span>}
          {optionalLabel && (
            <span className="shrink-0 text-xs font-normal text-[var(--chekin-color-gray-2)]">
              {optionalLabel}
            </span>
          )}
          {tooltip && <HelpTooltip content={tooltip} side="top" size={16} />}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--chekin-color-gray-2)]" />
        <LegacyInput
          {...props}
          type="text"
          disabled={isBlocked}
          placeholder={placeholderText}
          aria-busy={loading}
          aria-invalid={invalid}
          className={cn(
            'w-full py-3 pl-12 pr-10 text-base text-[var(--chekin-color-brand-navy)] focus:border-[var(--chekin-color-gray-3)]',
            (loading || onReset) && 'pr-20',
            invalid &&
              'border-[var(--error-message-color)] text-[var(--error-message-color)] placeholder:text-[var(--error-message-color)]',
            loading && 'cursor-progress',
            className,
          )}
        />
        {(loading || onReset) && (
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
            {loading && (
              <Loader2
                aria-hidden="true"
                className="h-5 w-5 animate-spin text-[var(--chekin-color-gray-2)]"
              />
            )}
            {onReset && (
              <Button
                variant="ghost"
                onClick={onReset}
                disabled={isBlocked}
                className="h-7 w-7 p-0 text-[var(--chekin-color-gray-2)]"
                aria-label={t('reset_search')}
              >
                <X className="h-5 w-5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
